import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Image } from 'cloudinary-react';
import { css } from '@emotion/core';

import config from 'config';
import Modal from 'common/modal';
import Markdown from 'common/markdown';
import Icon from 'common/icon';
import Button from 'common/button';
import cx from 'classnames';

const ContributionMatch = ({ isMatched, onChange, isOptional, message, terms, logo, name }) => {
    const [isViewingTerms, setViewingTerms] = useState(false);

    return (
        <div
            data-testid="sponsor-match"
            className={cx(
                'bg-blue-10 rounded-b-lg -mx-8 md:-mx-24 -mb-12 md:-mb-16 mt-12 md:mt-16 overflow-hidden',
                { 'px-8 md:px-24': !isOptional }
            )}
        >
            <div className="flex items-stretch">
                {isOptional && (
                    <button
                        type="button"
                        data-testid="match-toggle"
                        className={cx(
                            'flex-no-shrink flex-no-grow flex justify-center items-center w-24 transition',
                            {
                                'bg-blue-30': isMatched,
                                'bg-blue-50': !isMatched,
                            }
                        )}
                        onClick={() => {
                            if (!isOptional) return;
                            onChange(!isMatched);
                        }}
                    >
                        <div className="w-8 h-8 rounded-sm bg-white flex justify-center items-center">
                            <Icon
                                icon="check"
                                type="solid"
                                size="1x"
                                className={cx('text-blue-50 transition', {
                                    'opacity-0': !isMatched,
                                    'opacity-100': isMatched,
                                })}
                            />
                        </div>
                    </button>
                )}
                <div
                    className={cx('flex-grow flex flex-col justify-center items-start py-8', {
                        'pl-8': isOptional,
                    })}
                >
                    <p className="m-0 text-sm font-medium text-blue-80">{message}</p>
                    {terms && (
                        <button
                            type="button"
                            className="text-sm font-medium link-blue whitespace-no-wrap inline-block mt-2"
                            data-testid="match-terms"
                            onClick={() => setViewingTerms(true)}
                        >
                            View terms
                        </button>
                    )}
                </div>
                {logo && (
                    <div
                        className={cx(
                            'flex flex-no-shrink justify-center items-center pl-8 py-8 w-32 md:w-48',
                            { 'pr-8': isOptional }
                        )}
                    >
                        <Image publicId={logo} alt={name} className="block max-w-full" />
                    </div>
                )}
            </div>

            <Modal close={() => setViewingTerms(false)} isOpen={isViewingTerms}>
                <div className="p-14">
                    <Markdown
                        className="text-16 leading-loose"
                        css={css`
                            a {
                                color: ${config('/tailwind/colors/green50')};
                                &:hover {
                                    color: ${config('/tailwind/colors/green70')};
                                }
                            }
                        `}
                    >
                        {terms}
                    </Markdown>

                    <div className="mt-8">
                        <Button
                            color="green"
                            size="md"
                            onClick={() => setViewingTerms(false)}
                            className="text-16 font-medium block w-full"
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

ContributionMatch.propTypes = {
    isMatched: PropTypes.bool.isRequired,
    isOptional: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    terms: PropTypes.string,
    logo: PropTypes.string,
};

ContributionMatch.defaultProps = {
    terms: null,
    logo: null,
};

export default ContributionMatch;
